import { useContext, useEffect, useState } from 'react';
import { useSocket } from '../../context/socket';
import { AuthContext } from '../../context/AuthContext';

const MAX_MARKERS = 8;
const LOCAL_VISITOR_MARKER = {
  key: 'hitlab-local-visitor',
  active: true,
};
const GUEST_ATTRIBUTION_STORAGE_KEY = 'jg_guest_attrib';

const getGuestEntries = (payload = {}) => {
  const guests = payload.guests || payload.sessions || payload.guestSessions || {};
  if (Array.isArray(guests)) return guests;
  if (guests && typeof guests === 'object') {
    return Object.entries(guests).map(([sessionId, guest]) => ({
      ...(guest || {}),
      sessionId: guest?.sessionId || guest?.session_id || sessionId,
    }));
  }
  return [];
};

const getGuestKey = (guest = {}, index = 0) =>
  String(
    guest.sessionId ||
      guest.session_id ||
      guest.guestId ||
      guest._id ||
      guest.id ||
      `guest-${index}`,
  );

const getGuestCount = (payload = {}, entries = []) => {
  const count = Number(payload.count);
  if (payload.count !== undefined && Number.isFinite(count) && count >= 0) {
    return Math.floor(count);
  }
  return entries.length;
};

const buildMarkers = (entries = []) =>
  entries.slice(0, MAX_MARKERS).map((guest, index) => ({
    key: getGuestKey(guest, index),
    active: String(guest.activityType || guest.status || '').toLowerCase() !== 'background',
  }));

const withLocalVisitorFloor = (count) =>
  Math.max(1, Number.isFinite(Number(count)) ? Math.floor(Number(count)) : 1);

const getCountFromPayload = (payload = {}) => {
  const count = Number(payload.count);
  return payload.count !== undefined && Number.isFinite(count) && count >= 0
    ? Math.floor(count)
    : null;
};

const getRemovedGuestKeys = (removed = []) => {
  if (Array.isArray(removed)) {
    return removed.map((item, index) => {
      if (typeof item === 'string') return item;
      return getGuestKey(item, index);
    });
  }
  if (removed && typeof removed === 'object') return Object.keys(removed);
  return [];
};

const trimMarkersForCount = (markers = [], count = null) => {
  const realMarkers = markers.filter(
    (marker) => marker.key !== LOCAL_VISITOR_MARKER.key,
  );
  const limit =
    count === null
      ? MAX_MARKERS
      : Math.min(MAX_MARKERS, withLocalVisitorFloor(count));
  const trimmed = realMarkers.slice(0, limit);
  return trimmed.length ? trimmed : [LOCAL_VISITOR_MARKER];
};

const getGuestSocketAuth = () => {
  if (typeof window === 'undefined') return { guest: 'true' };
  const params = new URLSearchParams(window.location.search);
  const pickParam = (key) => params.get(key) || null;
  const locale = window.navigator?.language || '';
  const localeRegion =
    (locale.includes('-') && locale.split('-')[1]) ||
    (locale.includes('_') && locale.split('_')[1]) ||
    null;

  const guestAuth = {
    guest: 'true',
    utm_source: pickParam('utm_source'),
    utm_medium: pickParam('utm_medium'),
    utm_campaign: pickParam('utm_campaign'),
    utm_content: pickParam('utm_content'),
    region: pickParam('region') || localeRegion,
    referrer: document.referrer || null,
  };

  Object.keys(guestAuth).forEach((key) => {
    if (guestAuth[key] === null || guestAuth[key] === '') delete guestAuth[key];
  });

  return guestAuth;
};

export default function HitLabGalaxyGatePresence() {
  const { socket, getLatencyMs } = useSocket() || {};
  const { player } = useContext(AuthContext) || {};
  const [visitorCount, setVisitorCount] = useState(1);
  const [markers, setMarkers] = useState([LOCAL_VISITOR_MARKER]);

  useEffect(() => {
    if (!socket || typeof socket.on !== 'function' || typeof socket.off !== 'function') {
      return undefined;
    }

    let heartbeatId = null;
    let recovering = false;
    const isAuthenticated = !!player?.token;

    const configureGuestSocket = () => {
      if (isAuthenticated) return;
      const guestAuth = getGuestSocketAuth();
      const attribution = { ...guestAuth };
      delete attribution.guest;
      try {
        window.localStorage.setItem(
          GUEST_ATTRIBUTION_STORAGE_KEY,
          JSON.stringify(attribution),
        );
      } catch (err) {
        // Guest proof is supplemental; storage failures should not block the page.
      }

      if (socket.auth) {
        delete socket.auth.token;
      } else {
        socket.auth = {};
      }
      socket.auth = { ...(socket.auth || {}), ...guestAuth };
      if (!socket.connected && typeof socket.connect === 'function') {
        socket.connect();
      }
    };

    const emitHeartbeat = (activityType = 'active') => {
      if (typeof socket.emit !== 'function' || !socket.connected || !socket.id) {
        return;
      }
      const latencyMs = typeof getLatencyMs === 'function' ? getLatencyMs() : null;
      socket.emit('presence:heartbeat', {
        guest: true,
        sessionId: socket.id,
        activityType,
        source: 'hitlab_landing',
        latencyMs: Number.isFinite(Number(latencyMs))
          ? Math.round(Number(latencyMs))
          : null,
      });
    };

    const requestSnapshot = () => {
      if (typeof socket.emit !== 'function' || !socket.connected) return;
      socket.emit('guest:snapshot:request');
    };

    const startPresence = () => {
      const activityType =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(activityType);
      requestSnapshot();
      if (!heartbeatId) {
        heartbeatId = setInterval(() => {
          const nextActivity =
            document.visibilityState === 'visible' ? 'active' : 'background';
          emitHeartbeat(nextActivity);
        }, 10000);
      }
    };

    const stopPresence = () => {
      if (heartbeatId) {
        clearInterval(heartbeatId);
        heartbeatId = null;
      }
    };

    const handleConnect = () => startPresence();
    const handleDisconnect = () => stopPresence();
    const handleClose = () => stopPresence();
    const handleVisibility = () => {
      const activityType =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(activityType);
    };
    const handleFocus = () => emitHeartbeat('active');
    const handleBlur = () => emitHeartbeat('background');
    const handleGuestError = (err) => {
      if (!err?.message || !err.message.includes('unknown session')) return;
      stopPresence();
      if (recovering) return;
      recovering = true;
      if (typeof socket.disconnect === 'function') socket.disconnect();
      setTimeout(() => {
        if (typeof socket.connect === 'function') socket.connect();
        recovering = false;
      }, 200);
    };

    const handleSnapshot = (payload = {}) => {
      const entries = getGuestEntries(payload);
      const count = getGuestCount(payload, entries);
      setVisitorCount(withLocalVisitorFloor(count));
      const nextMarkers = buildMarkers(entries);
      setMarkers(trimMarkersForCount(nextMarkers, count));
    };

    const handleDiff = (payload = {}) => {
      const count = getCountFromPayload(payload);
      const added = getGuestEntries({ guests: payload.add || [] });
      const updated = getGuestEntries({ guests: payload.update || [] });
      const removedKeys = getRemovedGuestKeys(payload.remove);

      setVisitorCount((current) =>
        withLocalVisitorFloor(
          count !== null ? count : Number(current) + added.length - removedKeys.length,
        ),
      );

      if (added.length || updated.length || removedKeys.length || count !== null) {
        setMarkers((current) => {
          let next = current.filter(
            (marker) =>
              marker.key !== LOCAL_VISITOR_MARKER.key &&
              !removedKeys.includes(marker.key),
          );

          updated.forEach((guest, index) => {
            const marker = buildMarkers([guest], index)[0];
            if (!marker) return;
            const existingIndex = next.findIndex((item) => item.key === marker.key);
            if (existingIndex >= 0) {
              next[existingIndex] = { ...next[existingIndex], ...marker };
            }
          });

          if (added.length) {
            const incoming = buildMarkers(added);
            const incomingKeys = incoming.map((item) => item.key);
            next = [
              ...incoming,
              ...next.filter((marker) => !incomingKeys.includes(marker.key)),
            ];
          }

          return trimMarkersForCount(next, count);
        });
      }
    };

    const handleCount = (payload = {}) => {
      const count = getCountFromPayload(payload);
      if (count === null) return;
      setVisitorCount(withLocalVisitorFloor(count));
      setMarkers((current) => trimMarkersForCount(current, count));
    };

    try {
      configureGuestSocket();
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('close', handleClose);
      socket.on('guest:error', handleGuestError);
      socket.on('guest:snapshot', handleSnapshot);
      socket.on('guest:diff', handleDiff);
      socket.on('guest:count', handleCount);
      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);
      window.addEventListener('beforeunload', stopPresence);
      handleVisibility();
      if (socket.connected) startPresence();
    } catch (err) {
      return undefined;
    }

    return () => {
      try {
        stopPresence();
        document.removeEventListener('visibilitychange', handleVisibility);
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('close', handleClose);
        socket.off('guest:error', handleGuestError);
        socket.off('guest:snapshot', handleSnapshot);
        socket.off('guest:diff', handleDiff);
        socket.off('guest:count', handleCount);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('beforeunload', stopPresence);
        if (!isAuthenticated && !socket.auth?.token) socket.auth = {};
      } catch (err) {
        // Presence proof is supplemental; landing rendering must stay quiet.
      }
    };
  }, [getLatencyMs, player?.token, socket]);

  const displayVisitorCount = withLocalVisitorFloor(visitorCount);
  const visitorLabel = displayVisitorCount === 1 ? 'visitor' : 'visitors';
  const copy = `${displayVisitorCount.toLocaleString()} ${visitorLabel} at the Galaxy Gate`;

  return (
    <aside
      className="hitlab-galaxy-gate-presence"
      aria-label="Galaxy Gate visitor presence"
    >
      <div
        className="hitlab-galaxy-gate-sky"
        data-testid="hitlab-galaxy-gate-pulsar-stars"
        aria-hidden="true"
      >
        <span />
        <span />
        <span />
      </div>
      <div className="hitlab-galaxy-gate-copy">
        <span className="hitlab-galaxy-gate-dot" aria-hidden="true" />
        <div>
          <strong>{copy}</strong>
          <small>Live visitor signal from JG Universe.</small>
        </div>
      </div>
      {markers.length ? (
        <div
          className="hitlab-galaxy-gate-markers"
          aria-label="Recent Galaxy Gate visitor activity"
        >
          {markers.map((marker, index) => (
            <span
              key={`${marker.key}-${index}`}
              className={marker.active ? 'is-active' : ''}
              aria-hidden="true"
            />
          ))}
        </div>
      ) : null}
    </aside>
  );
}
